/* Career Navigation Quiz routing engine. No DOM access; safe to run in Node, the browser, or a Worker.
   Typed port of docs/reference/engine.js. Keep its behavior identical. */
import type { Quiz, State, RouteResult, Result } from "./types";

export function route(quiz: Quiz, state: State, qid: string, picked: string[]): RouteResult {
  const q = quiz.questions[qid];
  if (!q) throw new Error(`Unknown question ${qid}`);
  const chosen = q.options.filter(o => picked.includes(o.id));
  if (!chosen.length) throw new Error(`No option chosen for ${qid}`);
  if (q.select === "one" && chosen.length > 1) throw new Error(`${qid} allows one answer`);
  const s: State = {
    path: state.path,
    answers: { ...state.answers, [qid]: picked.slice() },
    history: [...state.history, qid],
    extraRelated: state.extraRelated.slice()
  };
  if (qid === quiz.config.startQuestion) s.path = chosen[0].path ?? null;

  if (q.select === "many") {
    const exclusive = chosen.some(o => o.exclusive);
    if (exclusive || chosen.length > 1) {
      s.extraRelated = chosen.filter(o => !o.exclusive).map(o => o.page!);
      return { state: s, page: q.multiPage! };
    }
    return { state: s, page: chosen[0].page! };
  }

  const opt = chosen[0];
  if (opt.page) return { state: s, page: opt.page };
  let next = opt.next || q.next;
  if (next && typeof next === "object") next = s.path ? next[s.path] : undefined;
  if (!next) throw new Error(`${qid}/${opt.id} has no route for path "${s.path}"`);
  return { state: s, next };
}

export function result(quiz: Quiz, state: State, primary: string): Result {
  const page = quiz.pages[primary];
  const order = (state.path && quiz.results.relatedOrder[state.path]) || quiz.results.relatedOrder.default;
  const hubPages = order.filter(h => h !== page.hub).map(h => quiz.hubs[h].page);
  const related = [...new Set([...state.extraRelated, ...hubPages])].filter(p => p !== primary);

  const tokens: Record<string, string> = { primary: page.title };
  for (const [qid, ids] of Object.entries(state.answers)) {
    if (ids.length !== 1) continue;
    const o = quiz.questions[qid].options.find(x => x.id === ids[0]);
    if (o && o.phrase) tokens[qid] = o.phrase;
  }
  const variants = (state.path && quiz.results.messages[state.path]) || [];
  const v = variants.find(x => x.requires.every(t => tokens[t]));
  const message = v ? v.text.replace(/\{(\w+)\}/g, (_, k: string) => tokens[k] || "") : "";
  return { primary, related, message, questionCount: state.history.length };
}

export function initialState(): State {
  return { path: null, answers: {}, history: [], extraRelated: [] };
}

/* Drive a full answer set to a result. Used by tests. */
export function run(quiz: Quiz, answers: Record<string, string[]>): Result {
  let state = initialState();
  let qid = quiz.config.startQuestion;
  for (let guard = 0; guard < 20; guard++) {
    if (!answers[qid]) throw new Error(`Answer set is missing ${qid}`);
    const r = route(quiz, state, qid, answers[qid]);
    state = r.state;
    if (r.page) return result(quiz, state, r.page);
    qid = r.next!;
  }
  throw new Error("Route did not terminate");
}
